import Command from '@ckeditor/ckeditor5-core/src/command';
import { findOptimalInsertionRange } from 'ckeditor5/src/widget';
import { getClassNames } from './util';

export default class VideoPropertiesCommand extends Command {
	constructor(editor) {
		super(editor);
		this.set('source');
		this.set('poster'); // The video poster
		this.set('videoPosition'); // The video position
		this.set('videoSize'); // the video size
		this.set('videoOptions', ['controls']); // the array of video options
	}

	refresh() {
		const model = this.editor.model;
		const selection = model.document.selection;
		const selectedMedia = this._getVideoElement(selection);

		// No need to do anything if it's not a video.
		if (!selectedMedia) return;

		// get source either from video tag directly or from attributes
		if (selectedMedia.childCount === 0) {
			this.source = [selectedMedia.getAttribute('data-source')];
			this.poster = selectedMedia.getAttribute('poster');
		} else {
			const videoElement = selectedMedia.getChild(0);
			const sourceHtml = videoElement
				.getAttribute('htmlContent');

			if (!sourceHtml) return;

			const videoTag = document.createElement('video');
			videoTag.innerHTML = sourceHtml;
			this.source = [
				videoTag.querySelector('source').getAttribute('src'),
			];
			const poster = videoElement.getAttribute('poster');
			this.poster = poster ? poster : videoElement.getAttribute('htmlAttributes').attributes.poster;
		}

		const videoPos = this._getState(selectedMedia, 'videoPosition');
		const videoSize = this._getState(selectedMedia, 'videoSize');
		const videoOptions = this._getVideoOptionsState(
			selectedMedia,
			'videoOptions'
		);

		if (!videoPos) this.videoPosition = '';
		else {
			this.videoPosition = videoPos;
			this.execute('videoPosition');
		}

		if (!videoSize) this.videoSize = '';
		else {
			this.videoSize = videoSize;
			this.execute('videoSize');
		}

		if (!videoOptions) this.videoOptions = ['controls'];
		else {
			this.videoOptions = videoOptions;
			this.execute('videoOptions');
		}

		this.isEnabled =
			isMediaSelected(selection) || isAllowedInParent(selection, model);
	}

	execute(attribute) {
		const model = this.editor.model;
		const selection = model.document.selection;
		const selectedMedia = this._getVideoElement(selection);

		if (selectedMedia) {
			model.change((writer) => {
				if (attribute === 'VIDEODELETE') {
					writer.remove(selectedMedia);
				} else {
					if (this[attribute]) {
						writer.setAttribute(
							attribute,
							this[attribute].toString(),
							selectedMedia
						);
					}
				}
			});
		}
	}

	_getVideoOptionsState(element, attribute) {
		if (!element) return null;

		const attributeValue = element.getAttribute(attribute);
		let result = [];

		if (!attributeValue) {
			// try to retrieve it from the htmlAttributes
			const videoElement = element.getChild(0);

			if (!videoElement) return null;

			const htmlAttributes =
				videoElement.getAttribute('htmlAttributes').attributes;

			if (!htmlAttributes) return null;

			for (const property in htmlAttributes) {
				if (property === 'poster') continue;
				result.push(property);
			}
		} else {
			result = attributeValue.split(',');
		}
		// if there's autoplay then restore it as data-autoplay
		const autoplayIndex = result.indexOf('autoplay');

		if (autoplayIndex !== -1) {
			result.splice(autoplayIndex, 1);
			result.push('data-autoplay');
		}
		return result;
	}

	_getState(element, attribute) {
		if (!element) return '';
		let result = element.getAttribute(attribute);

		if (!result) {
			// try to retrieve it from the htmlAttributes
			const htmlAttributes = element.getAttribute('htmlAttributes');

			if (!htmlAttributes || !htmlAttributes.classes) return '';

			const classes = htmlAttributes.classes;
			const classesList = getClassNames('attribute:' + attribute);

			for (const c of classes) {
				if (classesList.includes(c)) return c;
			}
		}
		return result;
	}

	// get video element from mdoel
	_getVideoElement(selection) {
		const element = selection.getSelectedElement();

		if (element) {
			if (element.name === 'htmlVideo') return element.parent;
			else if (element.name === 'video') return element;
		}
		return null;
	}
}

function isAllowedInParent(selection, model) {
	const insertionRange = findOptimalInsertionRange(selection, model);
	let parent = insertionRange.start.parent;

	if (parent.isEmpty && !model.schema.isLimit(parent)) {
		parent = parent.parent;
	}
	return model.schema.checkChild(parent, 'media');
}

function isMediaSelected(selection) {
	const element = selection.getSelectedElement();
	return !!element && element.name === 'media';
}
